/**
 * Plot orchestration. The three seams the controls call -
 * `doPlot` (fetch + render), `rerenderChartFromLast` (redraw cached data on a
 * presentation tweak), and `openFitPopover` (re-exported from `fit-popover.ts`).
 * Plus the legs/changeovers overlay fetches and the loading-ETA ticker.
 *
 * Export signatures here are a stable contract: `state/plot-state.ts`,
 * `plot-controls.ts`, `axis-bounds.ts`, and `series-picker.ts` import them.
 */

import { BUILDER_EMBED } from '../../state/embed.ts';
import { runPlot, getLegs, getChangeovers } from '../../api/endpoints.ts';
import { DEFAULT_SOURCE } from '../../state/data-sources.ts';
import {
  buildPlotRequest,
  plotRequestSignature,
  type PlotRequestOpts,
} from './plot-request.ts';
import {
  runtime,
  computeXExtent,
  resetZoomState,
  recordPlotTiming,
  estimatePlotSeconds,
} from './plot-runtime.ts';
import {
  plotFetching,
  plotEta,
  plottedSignature,
  lastPlotData,
  plottedSource,
  plotLoadStat,
  plotMissingStat,
  plotPointStat,
  legsLoading,
  changeoversLoading,
  chartEmpty,
  chartReady,
  plotXWindow,
} from './plot-ui-state.ts';
import { renderChart } from './render/render-chart.ts';
import { syncLiveTicking } from './live-tick.ts';
import { refreshChangeoverLegend } from './render/overlays.ts';
import { updateXWindowReadout } from './zoom.ts';
import { buildShaping, getFilters } from '../../state/request.ts';
import { gaugeSampled, setExactGauge, sliceSignature } from './gauge-slice.ts';
import { plotState } from '../../state/plot-state.ts';
import { buildQueryString } from '../../state/url-sync.ts';
import { recordRecent } from '../../state/recents.ts';
import { activeDataSource, selectedShip, startDate, endDate, plottedShipIds } from '../../state/store.ts';
import { shipLabel } from '../../utils/ships.ts';
import {
  fetchShipPlots,
  mergeBoxOverlay,
  mergeGaugeOverlay,
  mergeTableOverlay,
  mergeOverlay,
  supportsGrid,
  supportsOverlay,
  overlayCrowdingWarning,
  type ShipPlotResult,
} from './multi-ship-plot.ts';
import { showToast } from '../../ui/toast.ts';
import { showError, hideError } from '../../ui/error-box.ts';
import type { AnyPlotResponse, Changeover, HeatmapResponse, Leg } from '../../api/types.ts';
import { isXY } from '../../api/plot-shapes.ts';
import {
  comparisonIssues,
  mapElapsedWindow,
  periodComparison,
  resolveComparisonPeriods,
} from '../../state/period-comparison.ts';
import {
  fetchPeriodPanel,
  fetchPeriodPlots,
  periodRequestSignature,
  type PlotPanelResult,
} from './period-plot.ts';
import { userMessage } from '../../api/errors.ts';

export { openFitPopover } from './fit-popover.ts';

/**
 * Warn when heatmap panels were binned over different ranges.
 *
 * Each ship is binned over its own extent, so cell (3, 7) is not the same
 * operating point in two panels - side by side they invite a comparison the
 * grids do not support. Pinning axis bounds makes the edges identical, which is
 * why the request sends them through.
 */
function panelBinNote(results: ShipPlotResult[]): string | null {
  const grids = results
    .map((r) => r.data)
    .filter((d): d is HeatmapResponse => !!d && d.plot_type === 'heatmap');
  if (grids.length < 2) return null;
  const first = grids[0]!;
  const same = grids.every(
    (g) =>
      g.x_edges[0] === first.x_edges[0] &&
      g.x_edges[g.nx] === first.x_edges[first.nx] &&
      g.y_edges[0] === first.y_edges[0] &&
      g.y_edges[g.ny] === first.y_edges[first.ny],
  );
  if (same) return null;
  return 'Each panel is binned over its own range - set axis bounds to compare cell for cell.';
}

/* ── Loading ETA ────────────────────────────────────────── */

function startEtaTicker(): () => void {
  const etaSec = estimatePlotSeconds();
  const started = performance.now();
  const render = (): void => {
    const elapsed = Math.round((performance.now() - started) / 1000);
    if (etaSec != null) {
      const remain = etaSec - elapsed;
      plotEta.set(remain > 0 ? `~${remain}s` : `${elapsed}s…`);
    } else if (elapsed >= 2) {
      plotEta.set(`${elapsed}s`);
    } else {
      plotEta.set(null);
    }
  };
  render();
  const id = setInterval(render, 250);
  return () => clearInterval(id);
}

/**
 * A gauge sliced from thinned readings shows estimates. Once a slicer is let
 * go (or Play stops), ask once for the exact figures - the slicers sent as
 * filters, the readings kept - and draw those until the slicer moves again.
 * Nothing to do when the readings were whole: the browser's figures are exact.
 */
export async function settleGaugeSlices(): Promise<void> {
  const data = runtime.lastData;
  if (data?.plot_type !== 'gauge' || !gaugeSampled(data)) return;
  const signature = sliceSignature();
  if (signature === '[]') return;
  const results = await fetchShipPlots(plottedShipIds(), { state: plotState.value, shaping: buildShaping() });
  // Moved on while this ran: a newer slice or a new Plot owns the screen.
  if (runtime.lastData !== data || sliceSignature() !== signature) return;
  const exact = mergeGaugeOverlay(results);
  if (exact?.plot_type !== 'gauge') return;
  const result = data.ships
    ? { ...data, ships: data.ships.map((s, i) => ({ ...s, dials: exact.ships?.[i]?.dials ?? (i === 0 ? exact.dials : s.dials) })) }
    : { ...data, dials: exact.dials };
  setExactGauge(data, signature, result);
  rerenderChartFromLast();
}

/* ── doPlot ─────────────────────────────────────────────── */

export async function doPlot(opts?: PlotRequestOpts): Promise<void> {
  // A fetch is already running - remember the user wants another pass and exit;
  // the finally-block re-runs doPlot so rapid clicks/zooms never silently no-op.
  if (runtime.isFetching) {
    runtime.pendingReplot = true;
    return;
  }
  runtime.isFetching = true;
  plotFetching.set(true);
  const comparison = periodComparison.value;
  const periodPanels = comparison ? resolveComparisonPeriods(comparison) : [];
  const stopEta = comparison ? () => {} : startEtaTicker();

  resetZoomState();
  const built = buildPlotRequest(opts);
  runtime.currentRange = { start: built.body.start_date, end: built.body.end_date };

  // Multi-ship fans out one request per ship and merges; the single-ship path
  // below is untouched, so nothing changes for the common case.
  const shipIds = plottedShipIds();
  const multi = shipIds.length > 1;
  const overlayable = supportsOverlay(plotState.value.plot_type);
  // Heatmap fans out too, but only ever as panels - there is no overlay of it.
  const gridable = supportsGrid(plotState.value.plot_type);

  const startedAt = performance.now();
  try {
    let data: AnyPlotResponse | null;
    if (comparison) {
      const issues = comparisonIssues(comparison);
      if (issues.length) throw new Error(issues[0]!.message);
      if (built.body.plot_type !== 'line' && built.body.plot_type !== 'scatter') {
        throw new Error('Period comparison is available for Line and Scatter plots.');
      }
      const generation = ++runtime.comparisonGeneration;
      runtime.lastByShip = null;
      const results = await fetchPeriodPlots(periodPanels, undefined, (partial, completed) => {
        if (generation !== runtime.comparisonGeneration || periodComparison.value !== comparison) return;
        runtime.lastByPeriod = partial;
        plotEta.set(`${completed}/${periodPanels.length} periods`);
        renderChart(partial.find((result) => result.data)?.data ?? null);
      });
      // A stopped/replaced comparison may finish later; its headings and data
      // must never install beneath the new mode.
      if (generation !== runtime.comparisonGeneration || periodComparison.value !== comparison) return;
      runtime.lastByPeriod = results;
      runtime.periodBaseResults = results.map((result) => ({ ...result, panel: { ...result.panel } }));
      data = results.find((result) => result.data)?.data ?? null;
      runtime.lastData = data;
      plottedSource.set(built.body.source ?? DEFAULT_SOURCE);
      lastPlotData.set(data);
      plottedSignature.set(periodRequestSignature(periodPanels));
      renderChart(data);
      updateStatsFromPeriods(results, performance.now() - startedAt);
      const totalRows = results.reduce((total, result) => total + (result.data?.source_rows ?? 0), 0);
      recordPlotTiming(totalRows, performance.now() - startedAt);
      hideError();
      const warnings = results.map((result) => result.data?.warning).filter(Boolean);
      if (warnings.length) showToast('error', [...new Set(warnings)].join(' '));
      const s = plotState.value;
      if (s.plot_type === 'line' && s.show_legs) void maybeFetchLegsAndRender();
      if (s.plot_type === 'line' && s.show_changeovers) void maybeFetchChangeoversAndRender();
      return;
    } else if (multi && gridable) {
      runtime.lastByPeriod = null;
      runtime.periodBaseResults = null;
      runtime.comparisonGeneration++;
      const results: ShipPlotResult[] = await fetchShipPlots(shipIds, opts);
      runtime.lastByShip = results;
      if (plotState.value.plot_type === 'table') {
        // Tables are laid one under another with a Ship column, never joined.
        data = mergeTableOverlay(results);
        if (!data) {
          const why = results.find((r) => r.error)?.error;
          throw new Error(why ?? 'No data for the selected ships.');
        }
      } else if (plotState.value.plot_type === 'gauge') {
        // One canvas lays every ship's dials out; a failed ship stays as a row.
        data = mergeGaugeOverlay(results);
        if (!data) {
          const why = results.find((r) => r.error)?.error;
          throw new Error(why ?? 'No data for the selected ships.');
        }
      } else if (overlayable && plotState.value.plot_type === 'box') {
        // Boxes are summaries: the ships' boxes are set side by side, not joined.
        // Grid draws from lastByShip; the merged response serves the overlay.
        data = mergeBoxOverlay(results);
        if (!data) {
          const why = results.find((r) => r.error)?.error;
          throw new Error(why ?? 'No data for the selected ships.');
        }
      } else if (overlayable) {
        data = mergeOverlay(results);
        if (!data) {
          const why = results.find((r) => r.error)?.error;
          throw new Error(why ?? 'No data for the selected ships.');
        }
        // mergeOverlay only ever returns line/scatter, so `series` is always the
        // line count here.
        const crowding = overlayCrowdingWarning(data.series.length, shipIds.length);
        if (crowding) {
          data = { ...data, warning: data.warning ? `${data.warning} ${crowding}` : crowding };
        }
      } else {
        // Grid-only types have nothing to merge. The first ship's response
        // stands in for the shared readouts; the panels come from lastByShip.
        const first = results.find((r) => r.data)?.data ?? null;
        if (!first) {
          const why = results.find((r) => r.error)?.error;
          throw new Error(why ?? 'No data for the selected ships.');
        }
        data = first;
        const note = panelBinNote(results);
        if (note) data = { ...data, warning: data.warning ? `${data.warning} ${note}` : note };
      }
    } else {
      runtime.lastByPeriod = null;
      runtime.periodBaseResults = null;
      runtime.comparisonGeneration++;
      runtime.lastByShip = null;
      data = await runPlot(built.body);
      if (multi && !gridable) {
        // Histogram needs bin edges computed across ships (a backend change);
        // map draws through Leaflet and needs its own multi-track path. Say so
        // rather than silently showing one ship of the several selected.
        const note =
          `${plotState.value.plot_type} plots show one ship for now - ` +
          `showing ${shipLabel(shipIds[0] ?? '')} of ${shipIds.length} selected.`;
        data = { ...data, warning: data.warning ? `${data.warning} ${note}` : note };
      }
    }
    const elapsedMs = performance.now() - startedAt;
    runtime.lastData = data;
    plottedSource.set(built.body.source ?? DEFAULT_SOURCE);
    lastPlotData.set(data);
    // What is on screen now - the Plot button stays disabled until it diverges.
    plottedSignature.set(plotRequestSignature(built.body, shipIds));
    runtime.dataRange = computeXExtent(data);
    renderChart(data);
    updateStatsFromPlot(data, elapsedMs);
    recordPlotTiming(data.source_rows, elapsedMs);
    if (runtime.dataRange) {
      const isTime =
        isXY(data) && data.x_is_time;
      updateXWindowReadout(runtime.dataRange.min, runtime.dataRange.max, isTime, false);
    }
    if (data.warning) showToast('error', data.warning);
    hideError();

    // Home's recent-views strip. Recorded here - after the plot succeeded, and
    // only for EOS until recent views carry their source (EOS-3746). On the
    // next frame, so the capture reads the chart that was just drawn rather
    // than the one before it.
    if (activeDataSource.value === DEFAULT_SOURCE && !BUILDER_EMBED) {
      const drawn = shipIds.length ? shipIds : [selectedShip.value ?? ''].filter(Boolean);
      const q = buildQueryString();
      requestAnimationFrame(() =>
        recordRecent({ q, ships: drawn, plotType: plotState.value.plot_type }),
      );
    }

    const s = plotState.value;
    if (s.plot_type === 'line' && s.show_legs) void maybeFetchLegsAndRender();
    if (s.plot_type === 'line' && s.show_changeovers) void maybeFetchChangeoversAndRender();
  } catch (e) {
    // A failed attempt draws nothing, so leave the button clickable for a retry.
    plottedSignature.set(null);
    const msg = userMessage(e);
    showToast('error', 'Plot failed: ' + msg);
    showError('Plot failed: ' + msg);
  } finally {
    stopEta();
    runtime.isFetching = false;
    plotFetching.set(false);
    plotEta.set(null);
    if (runtime.pendingReplot) {
      runtime.pendingReplot = false;
      // Defer so the disabled state + uPlot rebuild settle before the next fetch.
      setTimeout(() => void doPlot(), 0);
    } else {
      // A Live chart on a rolling window keeps up from here; anything else stops.
      syncLiveTicking();
    }
  }
}

/**
 * What "drawn" means per plot type, for the Points readout. A heatmap returns
 * cells rather than points - reporting the occupied ones is the honest number,
 * and it is also the interesting one: 3,600 cells over 4 million rows.
 */
function drawnCount(d: AnyPlotResponse): number {
  if (d.plot_type === 'heatmap') return d.occupied_cells ?? 0;
  if (d.plot_type === 'correlation') return d.defined_cells ?? 0;
  if (d.plot_type === 'pie') return d.slices?.length ?? 0;
  if (d.plot_type === 'box') return d.boxes?.length ?? 0;
  if (d.plot_type === 'number') return d.value == null ? 0 : 1;
  if (d.plot_type === 'gauge') return (d.ships?.flatMap((s) => s.dials ?? []) ?? d.dials).filter((x) => x.value != null).length;
  if (d.plot_type === 'sankey') return d.dials.filter((x) => x.value != null).length;
  if (d.plot_type === 'table') return d.rows?.length ?? 0;
  return d.returned_points ?? 0;
}

/** Redraw the current chart from cached data (color/style/order tweaks). */
export function rerenderChartFromLast(): void {
  if (runtime.lastData || runtime.lastByPeriod) renderChart(runtime.lastData);
}

function updateStatsFromPeriods(results: PlotPanelResult[], elapsedMs: number): void {
  plotLoadStat.set({ ms: elapsedMs });
  const ready = results.filter((result) => result.data);
  plotPointStat.set({
    drawn: ready.reduce((total, result) => total + drawnCount(result.data!), 0),
    scanned: ready.reduce((total, result) => total + (result.data!.source_rows ?? 0), 0),
    partial: results.some((result) => result.status === 'error'),
    perShip: ready.map((result, index) => ({
      ship: `${String.fromCharCode(65 + index)} · ${result.panel.label}`,
      drawn: drawnCount(result.data!),
      scanned: result.data!.source_rows ?? 0,
    })),
  });
  plotMissingStat.set(null);
}

/** Retry one submitted slot without refetching successful periods. */
export async function retryPeriodPanel(panelId: string): Promise<void> {
  const existing = runtime.lastByPeriod?.find((result) => result.panel.id === panelId);
  if (!existing || existing.status === 'loading' || runtime.isFetching) return;
  const generation = runtime.comparisonGeneration;
  runtime.lastByPeriod = runtime.lastByPeriod!.map((result) => {
    if (result.panel.id !== panelId) return result;
    const { error: _error, ...withoutError } = result;
    return { ...withoutError, status: 'loading' };
  });
  rerenderChartFromLast();
  const fetched = await fetchPeriodPanel(existing.panel);
  if (generation !== runtime.comparisonGeneration || !runtime.lastByPeriod) return;
  runtime.lastByPeriod = runtime.lastByPeriod.map((result) =>
    result.panel.id === panelId ? fetched : result,
  );
  runtime.periodBaseResults = runtime.periodBaseResults?.map((result) =>
    result.panel.id === panelId ? fetched : result,
  ) ?? null;
  runtime.lastData = runtime.lastByPeriod.find((result) => result.data)?.data ?? null;
  lastPlotData.set(runtime.lastData);
  rerenderChartFromLast();
}

runtime.retryPeriodPanel = (panelId) => void retryPeriodPanel(panelId);

/**
 * Refine an elapsed zoom with one mapped UTC subrequest per intersecting period.
 * Base panel bounds and editor atoms never move; Reset reinstalls cached bases.
 */
export async function refinePeriodComparison(min: number, max: number): Promise<void> {
  const comparison = periodComparison.value;
  const bases = runtime.periodBaseResults;
  if (!comparison || !bases || runtime.isFetching || !(max > min)) return;
  runtime.isFetching = true;
  plotFetching.set(true);
  runtime.periodViewWindow = { min, max };
  const generation = ++runtime.comparisonGeneration;
  try {
    const mapped = bases.flatMap((base) => {
      const window = mapElapsedWindow(base.panel, min, max);
      return window ? [{ ...base.panel, ...window }] : [];
    });
    const fetched = await fetchPeriodPlots(mapped, undefined, (_partial, completed) => {
      plotEta.set(`${completed}/${mapped.length} periods`);
    });
    if (generation !== runtime.comparisonGeneration || periodComparison.value !== comparison) return;
    const byId = new Map(fetched.map((result) => [result.panel.id, result]));
    runtime.lastByPeriod = bases.map((base) => {
      const result = byId.get(base.panel.id);
      return result
        ? { ...result, panel: { ...base.panel } }
        : {
            panel: { ...base.panel },
            requestSignature: `${base.requestSignature}:empty-window`,
            status: 'empty' as const,
            data: null,
          };
    });
    runtime.lastData = runtime.lastByPeriod.find((result) => result.data)?.data ?? null;
    lastPlotData.set(runtime.lastData);
    renderChart(runtime.lastData);
    updateStatsFromPeriods(runtime.lastByPeriod, 0);
  } finally {
    // This refinement owns the global fetch flag. A mode switch invalidates its
    // generation, but must still release the flag or the next Plot click would
    // be queued behind a request that has already finished.
    runtime.isFetching = false;
    plotFetching.set(false);
    plotEta.set(null);
  }
}

export function resetPeriodComparisonView(): boolean {
  if (!runtime.lastByPeriod || !runtime.periodBaseResults) return false;
  runtime.periodViewWindow = null;
  runtime.lastByPeriod = runtime.periodBaseResults.map((result) => ({
    ...result,
    panel: { ...result.panel },
  }));
  runtime.lastData = runtime.lastByPeriod.find((result) => result.data)?.data ?? null;
  lastPlotData.set(runtime.lastData);
  plotXWindow.set(null);
  renderChart(runtime.lastData);
  return true;
}

/** Leave comparison explicitly and keep A's immutable response on screen. */
export function stopDisplayingPeriodComparison(): void {
  const reference = runtime.lastByPeriod?.find((result) => result.panel.id === 'period-a');
  runtime.comparisonGeneration++;
  runtime.lastByPeriod = null;
  runtime.periodBaseResults = null;
  runtime.periodViewWindow = null;
  runtime.lastData = reference?.data ?? null;
  lastPlotData.set(runtime.lastData);
  plottedSignature.set(
    runtime.lastData
      ? plotRequestSignature(buildPlotRequest().body, plottedShipIds())
      : null,
  );
  if (runtime.lastData) renderChart(runtime.lastData);
}

/** Hide suspended period data without discarding the cache used on resume. */
export function hidePeriodComparisonDisplay(message: string): void {
  if (!runtime.lastByPeriod) return;
  chartReady.set(false);
  chartEmpty.set({ text: 'Period comparison paused', sub: message });
}

/* ── Stats (plot tab: Load + Missing) ───────────────────── */

function updateStatsFromPlot(data: AnyPlotResponse, elapsedMs: number): void {
  plotLoadStat.set({ ms: elapsedMs });

  // Point counts come from the per-ship responses when there are several, so
  // the total moves as ships are added and the breakdown can name each one.
  const perShip = (runtime.lastByShip ?? [])
    .filter((r) => r.data)
    .map((r) => ({
      ship: r.ship,
      drawn: drawnCount(r.data!),
      scanned: r.data!.source_rows ?? 0,
    }));
  plotPointStat.set(
    perShip.length
      ? {
          drawn: perShip.reduce((n, p) => n + p.drawn, 0),
          scanned: perShip.reduce((n, p) => n + p.scanned, 0),
          perShip,
        }
      : {
          drawn: drawnCount(data),
          scanned: data.source_rows ?? 0,
          perShip: [],
        },
  );
  const nulls = ('null_counts' in data ? data.null_counts : null) ?? {};
  const cols = Object.keys(nulls);
  if (cols.length && data.source_rows) {
    let total = 0;
    const perSeries: string[] = [];
    for (const c of cols) {
      const n = nulls[c] || 0;
      total += n;
      const pct = data.source_rows ? (n / data.source_rows) * 100 : 0;
      perSeries.push(`${c}: ${n.toLocaleString()} (${pct.toFixed(1)}%)`);
    }
    const overall = (total / (data.source_rows * cols.length)) * 100;
    plotMissingStat.set({ pct: overall, total, title: perSeries.join('\n') });
  } else {
    plotMissingStat.set({ pct: 0, total: 0, title: '' });
  }
}

/* ── Overlay fetches (redraw in place, never rebuild) ───── */

function overlayReq(): { ship_id: string; start_date: string; end_date: string; filters?: ReturnType<typeof getFilters> } {
  return {
    ship_id: selectedShip.value ?? '',
    start_date: runtime.currentRange?.start ?? startDate.value,
    end_date: runtime.currentRange?.end ?? endDate.value,
    filters: getFilters(),
  };
}

/** True when the current draw is the per-ship grid rather than one chart. */
function inGrid(): boolean {
  return runtime.gridInstances.length > 0;
}

/**
 * Fetch an overlay for every plotted ship in parallel and cache it by ship.
 * Grid panels draw their own ship's bands, which is what makes these overlays
 * meaningful there - stacked on a shared overlay they would have no owner.
 */
async function fetchPerShip<T>(
  fetcher: (req: ReturnType<typeof overlayReq>) => Promise<{ supported: boolean } & Record<string, unknown>>,
  pick: (resp: Record<string, unknown>) => T[],
): Promise<Record<string, T[]>> {
  const out: Record<string, T[]> = {};
  const base = overlayReq();
  await Promise.all(
    plottedShipIds().map(async (ship) => {
      try {
        const resp = await fetcher({ ...base, ship_id: ship });
        if (resp.supported) out[ship] = pick(resp);
      } catch (e) {
        console.warn('Overlay fetch failed for', ship, e);
      }
    }),
  );
  return out;
}

async function fetchPerPeriod<T>(
  fetcher: (req: ReturnType<typeof overlayReq>) => Promise<{ supported: boolean } & Record<string, unknown>>,
  pick: (resp: Record<string, unknown>) => T[],
): Promise<Record<string, T[]>> {
  const out: Record<string, T[]> = {};
  const panels = runtime.lastByPeriod?.map((result) => result.panel) ?? [];
  await Promise.all(
    panels.map(async (panel) => {
      try {
        const resp = await fetcher({
          ship_id: panel.ship,
          start_date: panel.start,
          end_date: panel.end,
          filters: getFilters(),
        });
        if (resp.supported) out[panel.id] = pick(resp);
      } catch (error) {
        console.warn('Period overlay fetch failed for', panel.id, error);
      }
    }),
  );
  return out;
}

export async function maybeFetchLegsAndRender(): Promise<void> {
  // No chart yet → nothing to overlay; the next doPlot will fetch.
  if (!runtime.lastData || plotState.value.plot_type !== 'line') return;
  if (runtime.lastByPeriod) {
    legsLoading.set(true);
    try {
      runtime.legsByPanel = await fetchPerPeriod<Leg>(
        (req) => getLegs(req) as never,
        (response) => (response.legs as Leg[]) ?? [],
      );
      rerenderChartFromLast();
    } finally {
      legsLoading.set(false);
    }
    return;
  }
  if (inGrid()) {
    legsLoading.set(true);
    try {
      runtime.legsByShip = await fetchPerShip<Leg>(
        (req) => getLegs(req) as never,
        (r) => (r.legs as Leg[]) ?? [],
      );
      rerenderChartFromLast();
    } finally {
      legsLoading.set(false);
    }
    return;
  }
  legsLoading.set(true);
  try {
    const resp = await getLegs(overlayReq());
    runtime.lastLegs = resp.supported ? resp.legs : null;
    // Redraw in place - rebuilding would reset the x-scale and undo a drag-zoom.
    if (runtime.instance) runtime.instance.redraw();
    else if (runtime.lastData) renderChart(runtime.lastData);
  } catch (e) {
    console.warn('Legs fetch failed', e);
  } finally {
    legsLoading.set(false);
  }
}

export async function maybeFetchChangeoversAndRender(): Promise<void> {
  if (!runtime.lastData || plotState.value.plot_type !== 'line') return;
  if (runtime.lastByPeriod) {
    changeoversLoading.set(true);
    try {
      runtime.changeoversByPanel = await fetchPerPeriod<Changeover>(
        (req) => getChangeovers(req) as never,
        (response) => (response.changeovers as Changeover[]) ?? [],
      );
      rerenderChartFromLast();
    } finally {
      changeoversLoading.set(false);
    }
    return;
  }
  if (inGrid()) {
    changeoversLoading.set(true);
    try {
      runtime.changeoversByShip = await fetchPerShip<Changeover>(
        (req) => getChangeovers(req) as never,
        (r) => (r.changeovers as Changeover[]) ?? [],
      );
      rerenderChartFromLast();
    } finally {
      changeoversLoading.set(false);
    }
    return;
  }
  changeoversLoading.set(true);
  try {
    const resp = await getChangeovers(overlayReq());
    runtime.lastChangeovers = resp.supported ? resp.changeovers ?? [] : null;
    refreshChangeoverLegend();
    if (runtime.instance) runtime.instance.redraw();
    else if (runtime.lastData) renderChart(runtime.lastData);
  } catch (e) {
    console.warn('Changeovers fetch failed', e);
    runtime.lastChangeovers = null;
    refreshChangeoverLegend();
  } finally {
    changeoversLoading.set(false);
  }
}

/** Drop the leg overlay and redraw in place (toggle turned off). */
export function clearLegsOverlay(): void {
  runtime.legsByPanel = {};
  runtime.legsByShip = {};
  if (inGrid()) {
    rerenderChartFromLast();
    return;
  }
  runtime.lastLegs = null;
  if (runtime.instance) runtime.instance.redraw();
  else if (runtime.lastData) renderChart(runtime.lastData);
}

/** Drop the changeover overlay + legend and redraw in place (toggle off). */
export function clearChangeoversOverlay(): void {
  runtime.changeoversByPanel = {};
  runtime.changeoversByShip = {};
  if (inGrid()) {
    refreshChangeoverLegend();
    rerenderChartFromLast();
    return;
  }
  runtime.lastChangeovers = null;
  refreshChangeoverLegend();
  if (runtime.instance) runtime.instance.redraw();
  else if (runtime.lastData) renderChart(runtime.lastData);
}
